/// Sidebar.jsx
import React, { useState, useEffect, useRef, useContext } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFacebook,
  faInstagram,
  faLinkedin,
  faTwitter,
  faXTwitter,
  faTiktok,
} from '@fortawesome/free-brands-svg-icons';
import {
  faArrowLeft,
} from '@fortawesome/free-solid-svg-icons';
import stylesDesktop from './SidebarDesktop.module.css';
import stylesMobile from './SidebarMobile.module.css';
import SearchBar from 'components/navigation/SearchBar'; // Import du nouveau SearchBar
import NavItemPharos from './NavItemPharos';
import NavItem from './NavItem';
import api from 'services/api';
import { useNavigate, Link } from 'react-router-dom';
import { useSubSite } from 'contexts/SubSiteContext';
import useBreakpoint from 'hooks/useBreakpoint';
import ProfileButton from 'components/common/ProfileButton';
import useAccessibility from 'hooks/useAccessibility';
import useProfile from 'hooks/useProfile';
import { ProfileSidebarContext } from 'contexts/ProfileSidebarContext';
import { AccessibilitySidebarContext } from 'contexts/AccessibilitySidebarContext';
import AccessibilitySidebar from 'components/settings/AccessibilitySidebar';
import ProfileSidebar from 'components/settings/ProfileSidebar';
import buttons from 'components/common/Button.module.css';
import { AccessibilityOutline, BagOutline, PersonOutline } from 'react-ionicons';
import Spinner from 'components/common/Spinner';
import { useAuth } from "contexts/AuthContext";
import { useCart } from 'contexts/CartContext';

const Sidebar = ({ isOpen, onClose, onItemSelect, selectedItem, onCloseAllSidebars }) => {
  const isMobile = useBreakpoint();
  const styles = isMobile ? stylesMobile : stylesDesktop;

  const [menu, setMenu] = useState(null);
  const [loading, setLoading] = useState(true);
  const [liveMessage, setLiveMessage] = useState('');
  const firstNavItemRef = useRef(null);
  const [error, setError] = useState(null);

  // Profil actif
  const { profileOption } = useProfile();

  // Contextes pour les sidebars (desktop)
  const { openProfileSidebar } = useContext(ProfileSidebarContext);
  const { openAccessibilitySidebar } = useContext(AccessibilitySidebarContext);

  // États et handlers pour les sidebars mobiles
  const [isAccessibilitySidebarOpen, setAccessibilitySidebarOpen] = useState(false);
  const [isProfileSidebarOpen, setProfileSidebarOpen] = useState(false);

  const iconMap = {
    facebook: faFacebook,
    twitter: faXTwitter,
    instagram: faInstagram,
    linkedin: faLinkedin,
  };

  const [socialLinks, setSocialLinks] = useState([]);
  const navigate = useNavigate();

  const { isSubSite, subSiteSlug } = useSubSite();

  useEffect(() => {
    const fetchSocialLinks = async () => {
      try {
        // Utiliser le bon endpoint en fonction du sous-site
        const endpoint = isSubSite
          ? `/general-informations/social-networks/${subSiteSlug}`
          : '/general-informations/social-networks';

        const response = await api.get(endpoint);
        setSocialLinks(response.data);
      } catch (error) {
        console.error('Erreur lors de la récupération des réseaux sociaux:', error);
      }
    };

    fetchSocialLinks();
  }, [isSubSite, subSiteSlug]); // Mettre à jour les dépendances

  // useEffect(() => {
  //   const fetchMenu = async () => {
  //     try {
  //       const response = await api.get(
  //         isSubSite
  //           ? `/subsites/${subSiteSlug}/navigation/menu/main`
  //           : '/navigation/menu/main'
  //       );
  //       setMenu(response.data);
  //       setError(null);
  //     } catch (error) {
  //       console.error('Erreur lors de la récupération du menu:', error);
  //       setError('Impossible de charger le menu.');
  //     } finally {
  //       setLoading(false);
  //     }
  //   };
  //
  //   if (isOpen) {
  //     fetchMenu();
  //     firstNavItemRef.current?.focus();
  //   }
  // }, [isOpen, isSubSite, subSiteSlug]);
  const { isLoggedIn, logout } = useAuth(); // 🔥 attention, à déplacer EN DEHORS du useEffect
  const { emptyCart } = useCart();

  useEffect(() => {

    const baseMenu = [
      {
        id: 1,
        name: "Accueil",
        target: "_self",
        type: "internal",
        link: "/",
        icon: "home",
      },
      {
        id: 2,
        name: "Programme",
        target: "_self",
        type: "internal",
        link: "/programme",
        icon: "calendar",
      },
      {
        id: 3,
        name: "Panier",
        target: "_self",
        type: "internal",
        link: "/panier",
        icon: "panier",
      },
      {
        id: 4,
        name: "Mon compte",
        target: "_self",
        type: "internal",
        link: "/mon-compte",
        icon: "user",
      },
    ];

    if (isLoggedIn) {
      baseMenu.push({
        id: 99,
        name: "Déconnexion",
        target: "_self",
        link: "#",
        type: "action",
        onClick: () => {
          emptyCart();
          logout();
          window.location.href = '/login';
        },
      });
    }

    setMenu({ items: baseMenu });
    setLoading(false);
  }, [isLoggedIn]); // 👈 refresh au changement d’état


  // Fonction pour gérer la recherche depuis SearchBar
  const handleSearch = (searchTerm) => {
    const searchUrl = isSubSite
      ? `/${subSiteSlug}/recherches?q=${encodeURIComponent(searchTerm)}`
      : `/recherches?q=${encodeURIComponent(searchTerm)}`;
    navigate(searchUrl);
    onCloseAllSidebars();
  };

  // Fonction pour gérer le clic sur une suggestion
  const handleSuggestionClick = (suggestion) => {
    handleSearch(suggestion.label);
  };

  const getItemLink = (item) => item.link;


  const handleAccessibilityClick = () => {
    setAccessibilitySidebarOpen(true);
    openAccessibilitySidebar();
  };

  const handleCloseAccessibilitySidebar = () => {
    setAccessibilitySidebarOpen(false);
  };

  const handleProfileClick = () => {

    setProfileSidebarOpen(true);
    openProfileSidebar();

  };

  const handleCloseProfileSidebar = () => {
    setProfileSidebarOpen(false);
  };

  // Variantes du profil actif
  const profileVariants = {
    citoyen: { label: 'Personnaliser ma navigation', icon: <PersonOutline /> },
    touriste: { label: 'Profil actif : Touriste', icon: <PersonOutline /> },
    jeune: { label: 'Profil actif : Jeune', icon: <PersonOutline /> },
    senior: { label: 'Profil actif : Senior', icon: <PersonOutline /> },
    parent: { label: 'Profil actif : Parent', icon: <PersonOutline /> },
    professionnel: { label: 'Profil actif : Professionnel', icon: <PersonOutline /> },
  };

  const { label, icon } = profileVariants[profileOption] || profileVariants.citoyen;

  return (
    <>
      {isOpen && <div className={styles.overlay} onClick={onClose} aria-hidden="true"></div>}

      <div
        className={`${styles.sidebar} ${isOpen ? styles.open : ''}`}
        role="navigation"
        aria-hidden={!isOpen}
        aria-label="Menu de navigation"
      >
        {/* Conteneur pour le header de la sidebar */}
        <div className={styles.headerContainer}>
          {isMobile ? (
            <div className={styles.mobileCloseContainer}>
              <button className={styles.mobileCloseButton} onClick={onClose} aria-label="Fermer le menu">
                <div className={styles.mobileLineContainer}>
                  <span className={styles.mobileLine}></span>
                  <span className={styles.mobileLine}></span>
                </div>
                Fermer
              </button>
            </div>
          ) : (
            // Bouton "Fermer" pour le desktop
            <div className={styles.closeContainer}>
              <button className={styles.closeButton} onClick={onClose} aria-label="Fermer le menu">
                <div className={styles.lineContainer}>
                  <span className={styles.line}></span>
                  <span className={styles.line}></span>
                </div>
                Fermer
              </button>
            </div>
          )}
        </div>

        <nav className={styles.mainMenu} aria-label="Menu principal">
          <ul className={`list--none ${styles.navList}`}>
            {loading ? (
              <Spinner />
            ) : error ? (
              <p className={styles.error}>{error}</p>
            ) : (
              menu &&
              menu.items.map((item, index) => {
                if (item.type === "action") {
                  return (
                    <NavItem
                      key={item.id}
                      label={item.name}
                      isSelected={selectedItem && selectedItem.id === item.id}
                      ref={index === 0 ? firstNavItemRef : null}
                      slug={item.target}
                      context="sidebar"
                      type={item.type}
                      icon={item.icon}
                      to={item.link}
                      onClick={() => {
                        item.onClick();
                        onCloseAllSidebars();
                      }}
                    />
                  );
                }

                return (
                  <NavItem
                    key={item.id}
                    label={item.name}
                    isSelected={selectedItem && selectedItem.id === item.id}
                    ref={index === 0 ? firstNavItemRef : null}
                    slug={item.target}
                    context="sidebar"
                    type={item.type}
                    icon={item.icon}
                    to={item.link}
                    onClick={() => {
                      if (item.type === "category") {
                        onItemSelect(item);
                        setLiveMessage(`${item.name}, sous-menu ouvert`);
                      } else {
                        onCloseAllSidebars();
                      }
                    }}
                  />
                );
              })
            )}
          </ul>


          {isMobile && (
            <div className={styles.buttonContainer}>
              {/* Bouton d'accessibilité */}
              <button
                className={`${buttons.primary} ${buttons.btnOutline} ${styles.accessButton}`}
                onClick={handleAccessibilityClick}
                aria-label="Paramètres d'accessibilité"
                title="Paramètres d'accessibilité"
              >
                <AccessibilityOutline
                  height=""
                  width=""
                  color="currentColor"
                  className={`${styles.icon} ${styles.accessibilityIcon}`}
                />
                <span className={styles.buttonLabel}>Accessibilité</span>
              </button>

              {/* Bouton de personnalisation de profil */}
              {!isSubSite && (
                <ProfileButton
                  onClick={handleProfileClick}
                  ariaLabel={label}
                  title={label}
                  variant={profileOption || 'citoyen'}
                >
                  <PersonOutline
                    height=""
                    width=""
                    color="currentColor"
                    className={`${styles.icon} ${styles.customizeIcon}`}
                  />
                  <span className={styles.buttonLabel}>{label}</span>
                </ProfileButton>
              )}
            </div>
          )}

          

          <div className={styles.socialsContainer}>
            <Link to="arras.fr" target="_blank">
            <span className={styles.backToArras}>
              <FontAwesomeIcon icon={faArrowLeft} />
              Retour sur arras.fr
              </span>
          </Link>
            <p className={styles.socialsLabel}>Suivez-nous sur les réseaux :</p>
            <ul className={`list--none ${styles.socials}`}>
              {socialLinks.map((link) => (
                <li key={link.name}>
                  <a
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.socialsIcon}
                    aria-label={link.name}
                  >
                    <FontAwesomeIcon icon={iconMap[link.icon] || faTiktok} />
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </nav>

        {isMobile && (
          <button className={styles.closeButton} onClick={onClose} aria-label="Ferme le menu">
            <div className={styles.burger}>
              <span className={styles.line}></span>
              <span className={styles.line}></span>
            </div>
            <span className={styles.label}>Fermer</span>
          </button>
        )}

        {/* Sidebars spécifiques au mobile */}
        {isMobile && isAccessibilitySidebarOpen && (
          <AccessibilitySidebar
            onClose={handleCloseAccessibilitySidebar}
            onSave={(options) => console.log("Options d'accessibilité enregistrées:", options)}
          />
        )}

        {isMobile && isProfileSidebarOpen && !isSubSite && (
          <ProfileSidebar
            onClose={handleCloseProfileSidebar}
            onSave={(options) => console.log("Options de profil enregistrées:", options)}
          />
        )}
      </div>

      <div aria-live="polite" className="sr-only">
        {liveMessage}
      </div>
    </>
  );
};

Sidebar.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onItemSelect: PropTypes.func,
  selectedItem: PropTypes.object,
  onCloseAllSidebars: PropTypes.func,
};

export default Sidebar;
